import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch, Control } from 'react-hook-form';
import { dynamicSelectAdd } from './select-configuration';
import {
    Box,
    CircularProgress,
    Divider,
    IconButton,
    InputAdornment,
    MenuItem,
    MenuItemProps,
    OutlinedInput,
    Tooltip
} from '@mui/material';
import { debounce } from 'lodash';
import { sortBy } from "lodash";
import { useTranslation } from "react-i18next";
import { Add, Close } from '@mui/icons-material';

import Select from '../../../select/select';
import ChipOrPlaceholder from './chip-or-placeholder';
import Checkbox from '../../../checkbox/checkbox';
import Typography from '../../../typography/typography';
import Chip from '../../../chip/chip';
import { MoreIcon } from '../../../icons';
import { fetchApi } from '../../../../utils/api';
import './element.scss';
import { useAuth } from '@/contexts';
import { useApi, useLanguage } from '@/hooks';
import { useDefaultConfig } from '@/hooks/useSetDefaultConfig';
import { auth } from '../../../../constants/auth';


interface DynamicSelectProps {
  formControl: any;
  fieldArrayName: string;
  name: string;
  label?: string;
  placeholder?: string;
  defaultValue?: any;
  required?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  options?: Array<{ label: string; value: any }>;
  multiple?: boolean;
  searchable?: boolean;
  onSearch?: (query: string) => void;
}

// Types
interface IOption {
    id: string | number;
    value: string | number;
    label: string | React.ReactNode;
    disabled?: boolean;
    isGroup?: boolean;
    options?: IOption[];
    searchLabel?: string;
}

// Custom API fetch function type for parent components
interface CustomFetchApiParams {
    search?: string;
    filters?: any;
    limit: number;
    skip: number;
    select?: string;
    order?: string;
}

interface CustomFetchApiResponse {
    data: IOption[];
    pagination?: {
        totalCount: number;
    };
}

interface IMenuItemsProps extends MenuItemProps {
    options: IOption[];
    multiple: boolean;
    placeholder: string;
    with_checkboxes: boolean;
    values: string | string[];
    field: IDynamicSearchSelectProps;
    disabledIds: (string | number)[];
    onChange: (value: string | string[]) => void;
    handleChange?: (value: string | string[]) => void;
    hasParent: boolean;
    t: (key: string) => string;
    // onFooterClick?: () => void;
}

interface IDynamicSearchSelectProps {
    name: string;
    placeholder: string;
    label: string;
    size?: 'small' | 'medium';
    options: IOption[];
    formControl: Control<any>;
    defaultValue?: string | string[];
    fieldArrayName: string;
    id?: string;
    required?: boolean;
    is_multiselect?: boolean;
    position?: number;
    formType?: string;
    typeOfField?: string;
    disabled?: boolean;
    onChipRemove?: (value: string) => void;
    enable_footer?: boolean;
    footer_action?: string;
    onFooterClick?: () => void;
    is_loading?: boolean;
    selectedLabel?: string;
    isInternal?: boolean;
    disabledIds?: (string | number)[];
    getSelectedData?: (data: IOption | IOption[] | null, name: string) => void;
    attributes?: string[];
    handleChange?: (value: string | string[]) => void;
    hasParent?: boolean;
    sortOrder?: string;
    apiType?: string;
    handleOptions?: (value: any) => void;
    filterKey?: string;
    customeFilter?: Record<string, any>;
    refresh?: boolean;
    addType?: string;
    showCancelButton?: boolean;
    /** Custom API function to fetch options from parent component */
    customFetchApi?: (params: CustomFetchApiParams) => Promise<CustomFetchApiResponse>;
    isMergeOptions?: boolean;
    showItemTooltip?: boolean;
    modalComponentProps?: any;
}

interface IPaginationState {
    page: number;
    hasMore: boolean;
    isLoadingMore: boolean;
    total: number;
}

// Helper function to generate menu items
const generateMenuItems = ({
    options,
    multiple,
    placeholder,
    with_checkboxes,
    values,
    // field,
    disabledIds,
    onChange,
    handleChange,
    hasParent,
    t,
    // onFooterClick
}: IMenuItemsProps): React.ReactElement[] => {
    const itemDisabled = (id: string | number) => disabledIds?.includes(id);
    const items: React.ReactElement[] = [];

    if (!multiple) {
        items.push(
            <MenuItem disabled value='' key="placeholder" sx={{ opacity: `0 !important`, height: `0 !important`, p: `0 !important`, m: `0 !important` }}>
                <Typography type='s3' color='theme.secondary.1000'>
                    <em>{t(placeholder)}</em>
                </Typography>
            </MenuItem>
        );
    }
    const selectedValues = (Array.isArray(values) && values?.length > 0) ? values?.filter((value) => value) : values;

    const handleParentChange = (parentValue: string, childOptions: IOption[]) => {
        const childValues = childOptions.map((child) => child.value);
        const valueArray = Array.isArray(selectedValues) ? selectedValues : [selectedValues];

        const isParentSelected = valueArray.includes(parentValue);
        const updatedValues = isParentSelected
            ? valueArray.filter((value) => value !== parentValue && !childValues.includes(value))
            : [...new Set([...valueArray, parentValue, ...childValues])];

        onChange(updatedValues);
        handleChange?.(updatedValues);
    };

    const isAllOptionsSelected = (parentValue: string, childOptions: IOption[]) => {
        const childValues = childOptions.map((child) => child.value);
        const valueArray = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
        return childValues.every((childValue) => valueArray.includes(childValue));
    };

    const isSomeOptionsSelected = (childOptions: IOption[]) => {
        const childValues = childOptions.map((child) => child.value);
        const valueArray = Array.isArray(selectedValues) ? selectedValues : [selectedValues];
        return childValues.some((childValue) => valueArray.includes(childValue));
    };

    if (Array.isArray(options) && options.length > 0) {
        options.forEach((option) => {
            items.push(
                <MenuItem
                    value={option.value}
                    key={option.value}
                    disabled={Boolean(option.disabled) || itemDisabled(option.id || option.value)}
                    className={option.isGroup ? 'select--MenuItemHeader' : ''}
                >
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {multiple && with_checkboxes && (
                            <Checkbox
                                checked={
                                    hasParent
                                        ? isAllOptionsSelected(option.value, option.options || [])
                                        : Boolean(Array.isArray(selectedValues) ? selectedValues.includes(option.value) : selectedValues == option.value)
                                }
                                indeterminate={
                                    hasParent && isSomeOptionsSelected(option.options || [])
                                }
                                onChange={() => hasParent ? handleParentChange(option.value, option.options || []) : handleChange?.(option.value)}
                            />
                        )}
                        <Typography
                            type='s3'
                            color='theme.secondary.1000'
                            onClick={() => handleChange?.(option.value)}
                        >
                            {option.label}
                        </Typography>
                    </Box>
                </MenuItem>
            );

            if (option.isGroup && Array.isArray(option.options)) {
                option.options.forEach((opt) => {
                    items.push(
                        <MenuItem
                            value={opt.value}
                            disabled={Boolean(opt.disabled) || itemDisabled(opt.id || opt.value)}
                            sx={{ pl: 5 }}
                            key={`${option.value}-${opt.value}`}
                        >
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {multiple && with_checkboxes && (
                                    <Checkbox
                                        checked={Boolean(Array.isArray(values) && values.includes(opt.value))}
                                    />
                                )}
                                <Typography
                                    type='s3'
                                    color='theme.secondary.1000'
                                    onClick={() => handleChange?.(String(opt.value))}
                                >
                                    {opt.label == "Payment Entries" ? "Payment and Collection Entries" : opt.label || opt.value}
                                </Typography>
                            </Box>
                        </MenuItem>
                    );
                });
            }
        });
    }

    // if (field.enable_footer) {

    // }

    if (!items.length || (items.length == 1 && !multiple)) {
        items.push(
            <MenuItem disabled key="no-data">
                No data available
            </MenuItem>
        );
    }

    return items;
};

// Selected items component
const SelectedItems: React.FC<{
    data: (string | number)[];
    options: IOption[];
}> = ({ data, options }) => {
    const displayTitle = data.map(
        (selectedItem) =>
            options?.find((option) => option.value == selectedItem)?.label || ''
    );
    const filteredDisplayTitle = displayTitle.filter(Boolean);

    return (
        <Typography
            type='s4'
            color='inherit'
            sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}
        >
            {filteredDisplayTitle.slice(0, 4).join(', ')}
            {data.length > 4 && (
                <Chip variant='outlined' type='normal' label={<MoreIcon />} />
            )}
        </Typography>
    );
};

const Loading = () => (
    <Typography fontStyle='italic' color='inherit' type='s3'>Loading...</Typography>
)

const DynamicSearchSelect: React.FC<IDynamicSearchSelectProps> = React.memo((props) => {
    const watchedValue = useWatch({
        control: props.formControl,
        name: `${props.fieldArrayName}.${props.name}`
    });

    const { callApi } = useApi()
    // In MFE mode each federated module ships its own copy of react-hook-form,
    // so the FormContext created by the host's FormProvider is invisible here
    // and useFormContext() returns null.
    // The internal `control` object (passed as formControl prop) does NOT expose
    // setValue — that lives only on the useForm() return value. We build a
    // stable fallback using useCallback so that useDefaultConfig's useEffect
    // (which has setValue as a dependency) does NOT re-fire on every render.
    const formContext = useFormContext();
    const { setValue: contextSetValue } = formContext || {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mfeFallbackSetValue = useCallback((fieldPath: string, value: any) => {
        const ctrl = props.formControl as any;
        if (!ctrl) return;
        // Write into internal form values
        const parts = fieldPath.split('.');
        let obj = ctrl._formValues;
        for (let i = 0; i < parts.length - 1; i++) {
            if (!obj[parts[i]]) obj[parts[i]] = {};
            obj = obj[parts[i]];
        }
        obj[parts[parts.length - 1]] = value;
        // Notify RHF subscribers so React re-renders
        ctrl._subjects?.state?.next({ name: fieldPath, values: { ...ctrl._formValues } });
        // props.formControl is stable for the lifetime of the form
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.formControl]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setValue: any = contextSetValue ?? mfeFallbackSetValue;
    const { t } = useTranslation();
    const { isRtl } = useLanguage();
    const { user: authUser } = useAuth();
    const contextUser = useMemo(() => {
        if (authUser) return authUser;
        try {
            const stored = window.localStorage.getItem(auth.storageUserKeyName);
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    }, [authUser]);
    const selectRef = useRef<HTMLDivElement>(null);

    // State management
    const [options, setOptions] = useState<IOption[]>([]);
    const [selectedValue, setSelectedValue] = useState<any>(null);
    const [selectedOptions, setSelectedOptions] = useState<IOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const [isAlreadyLoaded, setIsAlreadyLoaded] = useState<boolean>(false);
    const [prevFilter, setPrevFilter] = useState<any>(null);
    const [initialLoading, setInitialLoading] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [searchInitiate, setSearchInitiate] = useState<boolean>(false);
    const [addConfig, setAddConfig] = useState<any>(null);

    // Modal states
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isAddLoading, setIsAddLoading] = useState<boolean>(false);


    // Pagination state
    const [pagination, setPagination] = useState<IPaginationState>({
        page: 1,
        hasMore: true,
        isLoadingMore: false,
        total: 0
    });

    // Props destructuring with defaults
    const {
        handleOptions,
        isInternal = false,
        disabledIds = [],
        getSelectedData,
        attributes = [],
        filterKey = 'id',
        customeFilter = {},
        refresh,
        handleChange,
        hasParent = false,
        sortOrder = 'id:-1',
        apiType,
        addType,
        showCancelButton = true,
        customFetchApi,
        isMergeOptions = false,
    } = props;

    const showItemTooltip = props.showItemTooltip ?? (apiType === 'salesItems');

    const limit = 25;

    // Memoized values
    // Memoized values
    const isInternalOptions = useMemo(() => {
        if (isMergeOptions && (apiType || customFetchApi)) return false;
        return (!apiType && !customFetchApi) || (isInternal ? (apiType || customFetchApi) && props.options?.length == 0 ? false : true : false);
    }, [isInternal, apiType, customFetchApi, props.options?.length, isMergeOptions]);


    //eslint-disable-next-line
    const cf = useMemo(() => customeFilter, [JSON.stringify(customeFilter)]);




    const { defaultOptions = [] } = useDefaultConfig({
        props,
        setValue,
        isInternalOptions,
        watchedValue
    });


    // Local search function
    const searchOptionsLocally = useCallback((searchTerm: string, value: any): IOption[] => {
        const currentOptions: IOption[] = (props.options && props.options.length > 0) ? props.options : (defaultOptions || []);
        if (!searchTerm) return currentOptions;

        const lowercasedSearchTerm = searchTerm.toLowerCase();

        const filteredOptions = hasParent
            ? currentOptions?.reduce((acc: IOption[], opt: IOption) => {
                const searchedOptions = opt.options?.filter((item: IOption) => {
                    const label = React.isValidElement(item.label) ? item.searchLabel : String(item.label);
                    return label?.toLowerCase().includes(lowercasedSearchTerm);
                });

                if (searchedOptions?.length) {
                    acc.push({ ...opt, options: searchedOptions });
                }

                return acc;
            }, []) || []
            : currentOptions?.filter((opt: IOption) => {
                const label = React.isValidElement(opt.label) ? opt.searchLabel : String(opt.label);
                return label?.toLowerCase().includes(lowercasedSearchTerm);
            }) || [];

        //eslint-disable-next-line
        return sortBySelectedValues(filteredOptions, value);
        //eslint-disable-next-line
    }, [props.options, hasParent, defaultOptions]);

    // Get unique options
    const getUniqueOptions = useCallback((data: IOption[]): IOption[] => {
        const uniqueOptions = [
            ...new Map(data?.map(item => [item.value, item])).values()
        ];
        return uniqueOptions;
    }, []);

    // Sort by selected values
    const sortBySelectedValues = useCallback((arrayToSort: IOption[], value: any): IOption[] => {
        let selectedValues = Array.isArray(value) ? value : [value];
        selectedValues = selectedValues.map((item) => Number(item));
        const sortedValue = sortBy(arrayToSort, (item) => {
            const index = selectedValues.indexOf(item.id || item.value);
            return index == -1 ? Infinity : index;
        });
        return sortedValue;
    }, []);

    // Filtered options with pagination support
    const filteredOptions = useMemo(() => {
        if (isInternalOptions) {
            return searchOptionsLocally(searchQuery, watchedValue);
        }
        const apiOptions = sortBySelectedValues(getUniqueOptions([...options, ...selectedOptions]), watchedValue);
        if (isMergeOptions) {
            const localOptions = searchOptionsLocally(searchQuery, watchedValue);
            return getUniqueOptions([...localOptions, ...apiOptions]);
        }
        return apiOptions;
        //eslint-disable-next-line
    }, [options, selectedOptions, isInternalOptions, props.options, watchedValue, searchQuery, searchOptionsLocally, sortBySelectedValues, getUniqueOptions, isMergeOptions]);

    // Helper to get selected item data for tooltip
    const getSelectedDataOptions = useMemo(() => {
        if (!watchedValue || !filteredOptions.length) return null;
        const selected = filteredOptions.find(opt =>
            opt.id === watchedValue || opt.value === watchedValue
        );
        return selected || null;
    }, [watchedValue, filteredOptions]);

    // API Functions
    const fetchOptions = useCallback(async (searchTerm = '', flt = cf, page = 1, append = false) => {
        // Skip if neither apiType nor customFetchApi is provided
        if (!apiType && !customFetchApi) return [];

        setLoading(true);
        if (page > 1) {
            setPagination(prev => ({ ...prev, isLoadingMore: true }));
        }

        try {
            let data: IOption[] = [];
            let paginationResult: { totalCount?: number } = {};

            // Use custom API if provided, otherwise use default fetchApi
            if (customFetchApi) {
                const response = await customFetchApi({
                    search: searchTerm,
                    filters: flt,
                    limit,
                    skip: (page - 1) * limit,
                    select: attributes?.join(','),
                    order: sortOrder
                });
                data = response?.data || [];
                paginationResult = response?.pagination || {};
            } else {
                const response = await fetchApi({
                    apiKey: apiType!,
                    filters: {
                        limit,
                        skip: (page - 1) * limit,
                        search: searchTerm,
                        filters: flt,
                        select: attributes,
                        order: sortOrder
                    }
                });
                data = response?.data || [];
                paginationResult = response?.pagination || {};
            }

            if (data && Array.isArray(data)) {
                const newOptions = append ? [...options, ...data] : data;

                updateOptions(newOptions, append);
                handleSelectedValue(data);
                // Update pagination
                setPagination(prev => ({
                    ...prev,
                    page,
                    hasMore: (paginationResult?.totalCount || 0) > (page * limit),
                    total: paginationResult?.totalCount || 0,
                    isLoadingMore: false
                }));
            }

            return data || [];
        } catch (error) {
            console.error('Error fetching options:', error);
            setPagination(prev => ({ ...prev, isLoadingMore: false }));
            return [];
        } finally {
            setLoading(false);
        }
        //eslint-disable-next-line
    }, [apiType, customFetchApi, options, attributes, sortOrder, limit, cf]);

    // Handle scroll pagination
    const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const isBottom = scrollHeight - scrollTop <= clientHeight + 10;

        if (isBottom && pagination.hasMore && !pagination.isLoadingMore && !isInternalOptions) {
            const nextPage = pagination.page + 1;
            fetchOptions(searchQuery, cf, nextPage, true);
        }
    }, [pagination.hasMore, pagination.isLoadingMore, pagination.page, isInternalOptions, searchQuery, cf, fetchOptions]);

    // Update options
    const updateOptions = useCallback((newOptions: IOption[], append = false) => {
        const oldOptions = append ? options : selectedOptions;
        const uniqueOptions = getUniqueOptions([...oldOptions, ...newOptions]);
        setOptions(uniqueOptions);
    }, [options, selectedOptions, getUniqueOptions]);

    // Handle selected value
    const handleSelectedValue = useCallback(async (updateOptions: IOption[]) => {
        if (!watchedValue) return;
        let filterValue = props.is_multiselect ? watchedValue : [watchedValue];
        filterValue = Array.isArray(filterValue) ? filterValue?.filter(i => i) : [];
        filterValue = Array.isArray(filterValue) ? filterValue?.map((item) => Number(item)) : [];
        const isInOptions = updateOptions?.filter((item) =>
            Array.isArray(filterValue) ? filterValue.includes(item.id || item.value) : (item[filterKey] || item.value || item.id) == filterValue
        );

        if (Array.isArray(isInOptions) && isInOptions.length > 0) {
            getSelectedData?.(props.is_multiselect ? isInOptions : isInOptions[0], props.name);
            return true;
        }
        return false;
        //eslint-disable-next-line
    }, [props.is_multiselect, props.name, watchedValue, filterKey, getSelectedData, filteredOptions]);

    // Fetch options by ID
    const fetchIdOptions = useCallback(async () => {
        if (open) return;
        if (await handleSelectedValue(filteredOptions)) return;

        const condition = props.is_multiselect ?
            Array.isArray(watchedValue) && watchedValue.length > 0 :
            Boolean(watchedValue);

        if (condition) {
            setInitialLoading(true);
            const response = await fetchOptions('', { ...cf, '&id.in': watchedValue }, 1, false);
            setInitialLoading(false);

            if (response.length == 0 && setValue && !isInternalOptions) {
                // setValue(`${props.fieldArrayName}.${props.name}`, props.is_multiselect ? [] : null);
            }
        }
        //eslint-disable-next-line
    }, [open, watchedValue, JSON.stringify(cf)]);

    // Initial fetch
    const initialFetch = useCallback(async () => {
        const condition = JSON.stringify(cf) !== JSON.stringify(prevFilter);

        if (condition) {
            setPrevFilter(cf);
            setOptions([]);
            setIsAlreadyLoaded(false);
            setPagination({
                page: 1,
                hasMore: true,
                isLoadingMore: false,
                total: 0
            });
        }

        if (!isInternalOptions && !isAlreadyLoaded) {
            await fetchOptions(searchQuery, cf, 1, false);
            setIsAlreadyLoaded(true);
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cf, isInternalOptions, isAlreadyLoaded]);

    // Debounced search
    const debouncedSearch = useMemo(
        () => debounce(async (searchTerm: string) => {
            if (loading) return;

            if (!searchTerm && !isInternalOptions && searchInitiate) {
                setPagination({
                    page: 1,
                    hasMore: true,
                    isLoadingMore: false,
                    total: 0
                });
                await fetchOptions('', cf, 1, false);
            }
            if (searchTerm.length > 1 && !isInternalOptions) {
                setPagination({
                    page: 1,
                    hasMore: true,
                    isLoadingMore: false,
                    total: 0
                });
                await fetchOptions(searchTerm, cf, 1, false);
            }
        }, 700),
        //eslint-disable-next-line
        [isInternalOptions, searchInitiate, cf]
    );

    // Reset value function
    const resetValue = useCallback((field: any) => {
        field.onChange(props.is_multiselect ? [] : null);
        if (getSelectedData) {
            getSelectedData(null, props.name);
        }
    }, [props.is_multiselect, props.name, getSelectedData]);

    // Handle close
    const handleClose = useCallback(() => {
        setOpen(false);
        if (searchQuery.length > 0) {
            setIsAlreadyLoaded(false);
        }
        setSearchInitiate(false);
        setSearchQuery('');
    }, [searchQuery]);

    const handleModalOpen = useCallback(() => {
        setIsModalOpen(true);
    }, []);



    // Handle modal save
    const handleModalSave = useCallback(async (payload: any) => {
        if (!addConfig || !addConfig.api) {
            console.error('Add configuration is not properly set');
            return;
        }


        callApi({
            apiFunc: () => addConfig.api(payload),
            successMessage: `${props.label} ${t('common.added_successfully')}`,
            loadingState: setIsAddLoading,
            onSuccess: async (result) => {
                setIsModalOpen(false);
                if (!isInternalOptions) {
                    setPagination({
                        page: 1,
                        hasMore: true,
                        isLoadingMore: false,
                        total: 0
                    });
                    setIsAlreadyLoaded(false);
                    await fetchOptions('', cf, 1, false);
                }
                if (result && result.data) {
                    const newValue = props.is_multiselect
                        ? [...(watchedValue || []), result.data.id]
                        : result.data.id;
                    setValue(`${props.fieldArrayName}.${props.name}`, newValue);
                    if (getSelectedData) {
                        getSelectedData(result.data, props.name, true);
                    }
                }
            },
            onError: () => {
                setIsAddLoading(false);
            }
        })



    }, [addConfig, isInternalOptions, fetchOptions, cf, props.is_multiselect, props.fieldArrayName, props.name, watchedValue, setValue]);

    // Effects
    useEffect(() => {
        if (handleOptions) {
            handleOptions(selectedValue);
        }
    }, [handleOptions, selectedValue]);

    useEffect(() => {
        if (open) {
            initialFetch();
        }
    }, [open, initialFetch]);

    useEffect(() => {
        fetchIdOptions();
    }, [fetchIdOptions]);

    useEffect(() => {
        if (refresh) {
            setPagination({
                page: 1,
                hasMore: true,
                isLoadingMore: false,
                total: 0
            });
            fetchOptions('', cf, 1, false);
        }
    }, [refresh, fetchOptions, cf]);

    useEffect(() => {
        if (open) {
            debouncedSearch(searchQuery);
        }
        return () => debouncedSearch.cancel();
    }, [searchQuery, open, debouncedSearch]);

    useEffect(() => {
        if (watchedValue && !open) {
            const selectedData = options?.filter((item) =>
                props.is_multiselect ?
                    Array.isArray(watchedValue) && watchedValue.includes(item.id || item.value) :
                    (item.id || item.value) == watchedValue
            );
            setSelectedOptions(selectedData || []);
        }
    }, [watchedValue, options, props.is_multiselect]);

    const removeUndefined = useCallback((selectOptions: any[]) => {
        return Array.isArray(selectOptions) ? selectOptions.filter(Boolean) : [];
    }, []);

    useEffect(() => {
        const config = dynamicSelectAdd(addType);
        setAddConfig(config);
    }, [addType])



    return (
        <Controller
            control={props.formControl}
            name={`${props.fieldArrayName}.${props.name}`}
            render={({ field, fieldState }) => (
                <>
                    {props.placeholder == 'Select Currency' && setSelectedValue(field.value)}
                    {showItemTooltip ? (
                        <Tooltip
                            title={getSelectedDataOptions?.label ? String(getSelectedDataOptions.label) : ''}
                            placement='top'
                            arrow
                            slotProps={{
                                popper: {
                                    modifiers: [{ name: 'offset', options: { offset: [0, -25] } }],
                                },
                                tooltip: {
                                    sx: { maxWidth: 500 },
                                },
                            }}
                        >
                            <span>
                                <Select
                                    {...field}
                                    ref={selectRef}
                                    open={open}
                                    onOpen={() => {
                                        setOpen(true);
                                        setTimeout(() => {
                                            const searchInput = document.querySelector(
                                                `input[placeholder="Search ${props.label}"]`
                                            ) as HTMLInputElement;
                                            searchInput?.focus();
                                        }, 150);
                                    }}
                                    onClose={handleClose}
                                    fullWidth
                                    dataPosition={props.position}
                                    dataName={`${props.fieldArrayName}.${props.name}`}
                                    placeholder={props.placeholder}
                                    label={props.label}
                                    defaultValue={props.defaultValue || (props.is_multiselect ? [] : '')}
                                    searchValue={searchQuery}
                                    required={props.formType == 'builder' ? false : props.required}
                                    size={props.size || 'small'}
                                    multiple={props.is_multiselect || false}
                                    disabled={props.disabled}
                                    sx={(props.is_multiselect && field?.value?.length) ? { "& .MuiSelect-select": { padding: '5.5px !important' } } : {}}
                                    variant='outlined'
                                    value={
                                        initialLoading
                                            ? (props.is_multiselect ? [] : '')
                                            : (field.value?.id || field.value || (props.is_multiselect ? [] : ''))
                                    }
                                    className={
                                        props.is_multiselect && removeUndefined(field.value)?.length
                                            ? 'MultiSelect filter-select'
                                            : ''
                                    }
                                    MenuProps={{
                                        PaperProps: {
                                            onScroll: handleScroll,
                                            style: { maxHeight: 300 }
                                        }
                                    }}

                                    renderValue={(selected: any) => {
                                        if (Array.isArray(selected)) {
                                            return (
                                                <ChipOrPlaceholder
                                                    selectedLabel={props.selectedLabel}
                                                    data={removeUndefined(selected)}
                                                    placeholder={(loading || props.is_loading) ? <Loading /> : `${t('common.search')} ${props.label}`}
                                                    onDelete={props.onChipRemove}
                                                    disabled={props.disabled}
                                                    chipProps={{
                                                        sx: {
                                                            background: '#ebf9f2',
                                                            border: 0,
                                                            // borderRadius: '15px',
                                                            '& .MuiTypography-body1': {
                                                                color: '#289b64',
                                                                fontWeight: "500",
                                                            }
                                                        }
                                                    }}
                                                />
                                            );
                                        } else {
                                            const selectedOption = filteredOptions.find(
                                                (option) => (option.value?.id || option.value) == selected
                                            );
                                            return (loading || props.is_loading) ? <Loading /> : selectedOption?.label ||
                                                (typeof props.label == 'string' ?
                                                    `${t('common.search')} ${props.label}` :
                                                    props.placeholder);
                                        }
                                    }}
                                    searchPlaceholder={`${t('common.search')} ${props.label}`}
                                    handleSearch={(text: string) => {
                                        setSearchQuery(text);
                                        setSearchInitiate(true);
                                    }}
                                    error={Boolean(fieldState.error?.message)}
                                    helperText={fieldState.error?.message || ''}
                                    onBlur={field.onBlur}
                                    input={
                                        <OutlinedInput
                                            label=''
                                            endAdornment={
                                                <InputAdornment position='end'>
                                                    {(loading || props.is_loading) ? (
                                                        <CircularProgress
                                                            size={20}
                                                            sx={{ mr: 3, color: "#4AC08C" }}
                                                        />
                                                    ) : (
                                                        !props.disabled &&
                                                        (props.is_multiselect ?
                                                            (Array.isArray(field.value) && field.value.length > 0) :
                                                            Boolean(field.value)) && showCancelButton && (
                                                            <IconButton
                                                                aria-label='clear selection'
                                                                onClick={() => resetValue(field)}
                                                                edge='end'
                                                                sx={isRtl ? { ml: 2.5 } : { mr: 1.5 }}
                                                                size='small'
                                                            >
                                                                <Close sx={{ fontSize: '1rem' }} />
                                                            </IconButton>
                                                        )
                                                    )}
                                                </InputAdornment>
                                            }
                                        />
                                    }
                                >
                                    {generateMenuItems({
                                        options: filteredOptions,
                                        multiple: Boolean(props.is_multiselect),
                                        placeholder: props.placeholder,
                                        values: field.value,
                                        with_checkboxes: true,
                                        field: props,
                                        disabledIds,
                                        onChange: field.onChange,
                                        handleChange,
                                        hasParent,
                                        t,
                                        // onFooterClick: () => {
                                        //     if (props?.onFooterClick) {
                                        //         props?.onFooterClick();
                                        //     } else {
                                        //         handleModalOpen();
                                        //     }
                                        // }
                                    })}
                                    {props?.enable_footer &&
                                        <Box key="footer" sx={{ position: 'sticky', bottom: 0, background: '#fff', zIndex: 1 }}>
                                            <Divider flexItem sx={{ mt: '0.25rem !important', mb: '0.25rem !important' }} />
                                            <MenuItem sx={{ gap: 1 }} onClick={
                                                () => {
                                                    if (props?.onFooterClick) {
                                                        props?.onFooterClick();
                                                    } else {
                                                        handleModalOpen();
                                                    }
                                                }

                                            }>
                                                <Add fontSize='small' />
                                                <Typography type='s4' color='theme.secondary.1000'>
                                                    Create New {props.label}
                                                </Typography>
                                            </MenuItem>
                                        </Box>}
                                    {pagination.isLoadingMore && (
                                        <MenuItem disabled>
                                            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                                <CircularProgress size={20} />
                                            </Box>
                                        </MenuItem>
                                    )}
                                </Select>
                            </span>
                        </Tooltip>
                    ) : (
                        <Select
                            {...field}
                            ref={selectRef}
                            open={open}
                            onOpen={() => {
                                setOpen(true);
                                setTimeout(() => {
                                    const searchInput = document.querySelector(
                                        `input[placeholder="Search ${props.label}"]`
                                    ) as HTMLInputElement;
                                    searchInput?.focus();
                                }, 150);
                            }}
                            onClose={handleClose}
                            fullWidth
                            dataPosition={props.position}
                            dataName={`${props.fieldArrayName}.${props.name}`}
                            placeholder={props.placeholder}
                            label={props.label}
                            defaultValue={props.defaultValue || (props.is_multiselect ? [] : '')}
                            searchValue={searchQuery}
                            required={props.formType == 'builder' ? false : props.required}
                            size={props.size || 'small'}
                            multiple={props.is_multiselect || false}
                            disabled={props.disabled}
                            sx={(props.is_multiselect && field?.value?.length) ? { "& .MuiSelect-select": { padding: '5.5px !important' } } : {}}
                            variant='outlined'
                            value={
                                initialLoading
                                    ? (props.is_multiselect ? [] : '')
                                    : (field.value?.id || field.value || (props.is_multiselect ? [] : ''))
                            }
                            className={
                                props.is_multiselect && removeUndefined(field.value)?.length
                                    ? 'MultiSelect filter-select'
                                    : ''
                            }
                            MenuProps={{
                                PaperProps: {
                                    onScroll: handleScroll,
                                    style: { maxHeight: 300 }
                                }
                            }}

                            renderValue={(selected: any) => {
                                if (Array.isArray(selected)) {
                                    return (
                                        <ChipOrPlaceholder
                                            selectedLabel={props.selectedLabel}
                                            data={removeUndefined(selected)}
                                            placeholder={(loading || props.is_loading) ? <Loading /> : `${t('common.search')} ${props.label}`}
                                            onDelete={props.onChipRemove}
                                            disabled={props.disabled}
                                            chipProps={{
                                                sx: {
                                                    background: '#ebf9f2',
                                                    border: 0,
                                                    // borderRadius: '15px',
                                                    '& .MuiTypography-body1': {
                                                        color: '#289b64',
                                                        fontWeight: "500",
                                                    }
                                                }
                                            }}
                                        />
                                    );
                                } else {
                                    const selectedOption = filteredOptions.find(
                                        (option) => (option.value?.id || option.value) == selected
                                    );
                                    return (loading || props.is_loading) ? <Loading /> : selectedOption?.label ||
                                        (typeof props.label == 'string' ?
                                            `${t('common.search')} ${props.label}` :
                                            props.placeholder);
                                }
                            }}
                            searchPlaceholder={`${t('common.search')} ${props.label}`}
                            handleSearch={(text: string) => {
                                setSearchQuery(text);
                                setSearchInitiate(true);
                            }}
                            error={Boolean(fieldState.error?.message)}
                            helperText={fieldState.error?.message || ''}
                            onBlur={field.onBlur}
                            input={
                                <OutlinedInput
                                    label=''
                                    endAdornment={
                                        <InputAdornment position='end'>
                                            {(loading || props.is_loading) ? (
                                                <CircularProgress
                                                    size={20}
                                                    sx={{ mr: 3, color: "#4AC08C" }}
                                                />
                                            ) : (
                                                !props.disabled &&
                                                (props.is_multiselect ?
                                                    (Array.isArray(field.value) && field.value.length > 0) :
                                                    Boolean(field.value)) && showCancelButton && (
                                                    <IconButton
                                                        aria-label='clear selection'
                                                        onClick={() => resetValue(field)}
                                                        edge='end'
                                                        sx={isRtl ? { ml: 2.5 } : { mr: 1.5 }}
                                                        size='small'
                                                    >
                                                        <Close sx={{ fontSize: '1rem' }} />
                                                    </IconButton>
                                                )
                                            )}
                                        </InputAdornment>
                                    }
                                />
                            }
                        >
                            {generateMenuItems({
                                options: filteredOptions,
                                multiple: Boolean(props.is_multiselect),
                                placeholder: props.placeholder,
                                values: field.value,
                                with_checkboxes: true,
                                field: props,
                                disabledIds,
                                onChange: field.onChange,
                                handleChange,
                                hasParent,
                                t,
                                // onFooterClick: () => {
                                //     if (props?.onFooterClick) {
                                //         props?.onFooterClick();
                                //     } else {
                                //         handleModalOpen();
                                //     }
                                // }
                            })}
                            {props?.enable_footer &&
                                <Box key="footer" sx={{ position: 'sticky', bottom: 0, background: '#fff', zIndex: 1 }}>
                                    <Divider flexItem sx={{ mt: '0.25rem !important', mb: '0.25rem !important' }} />
                                    <MenuItem sx={{ gap: 1 }} onClick={
                                        () => {
                                            if (props?.onFooterClick) {
                                                props?.onFooterClick();
                                            } else {
                                                handleModalOpen();
                                            }
                                        }

                                    }>
                                        <Add fontSize='small' />
                                        <Typography type='s4' color='theme.secondary.1000'>
                                            Create New {props.label}
                                        </Typography>
                                    </MenuItem>
                                </Box>}
                            {pagination.isLoadingMore && (
                                <MenuItem disabled>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                        <CircularProgress size={20} />
                                    </Box>
                                </MenuItem>
                            )}
                        </Select>
                    )}

                    {/* Modal for adding new items */}
                    {addConfig && addConfig.component && (
                        <>
                            {React.createElement(addConfig.component, {
                                isOpen: isModalOpen,
                                setIsOpen: setIsModalOpen,
                                isLoading: isAddLoading,
                                onSave: handleModalSave,
                                modalProps: {
                                    maxWidth: 'md',
                                    fullWidth: true
                                },
                                ...props?.modalComponentProps
                            })}
                        </>
                    )}

                    {props.is_multiselect && Array.isArray(field.value) && (
                        <SelectedItems
                            data={field.value}
                            options={filteredOptions}
                        />
                    )}
                </>
            )}
        />
    );
});
export default DynamicSearchSelect;